import { useRef } from "react";
import type { StampCanvasObject, StampKind } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: StampCanvasObject;
}

// スタンプ（絵文字風アイコン）オブジェクト。
// - 5 種類のうちの 1 種を 1 文字の絵文字グリフとして描画する
// - ペンツール中はクリックが描画優先のため pointer-events を切る
// - 他の図形と同様に選択中はアクセントカラーの枠を出す
export function StampObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // 文字サイズは外接矩形の短辺に合わせ、リサイズに追従させる。
  const fontPx = Math.max(8, Math.floor(Math.min(object.w, object.h) * 0.85));
  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="stamp"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        cursor: "move",
        outline: isSelected ? "2px solid var(--color-accent)" : "none",
        outlineOffset: "2px",
        display: "grid",
        placeItems: "center",
        userSelect: "none",
        // 絵文字は OS のカラー絵文字フォントへフォールバックさせる。
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", "Segoe UI Symbol", sans-serif',
        lineHeight: 1,
        fontSize: `${fontPx}px`,
      }}
      aria-label={`スタンプ: ${STAMP_KIND_LABEL[object.stampKind]}`}
    >
      <span aria-hidden="true">{STAMP_KIND_GLYPH[object.stampKind]}</span>
    </div>
  );
}

/** スタンプ種別 → 表示に使う絵文字グリフ。 */
export const STAMP_KIND_GLYPH: Record<StampKind, string> = {
  "thumbs-up": "👍",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
};

/** スタンプ種別 → アクセシビリティ用の日本語ラベル。 */
export const STAMP_KIND_LABEL: Record<StampKind, string> = {
  "thumbs-up": "いいね",
  fire: "炎",
  smile: "笑顔",
  hundred: "100点",
  plus: "プラス",
};
